import { XIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { Field, FilterSelect } from "~/components/filter-controls";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { CALLBACK_EVENT_TYPES } from "~/lib/enums";
import { useStorefronts } from "~/lib/queries";
import type { CallbackSearch } from "./-search";

/**
 * The filter bar over the callback deliveries table.
 *
 * Status lives in the chip row above the table, not here, so the
 * exhausted-first default reads as a pressed button rather than hiding in a
 * dropdown.
 */
export function CallbackFilterBar({
  search,
  onChange,
  onClear,
  hasFilters,
}: {
  search: CallbackSearch;
  onChange: (patch: Partial<CallbackSearch>) => void;
  onClear: () => void;
  hasFilters: boolean;
}) {
  // Enough for every storefront this system has; the API caps a page at 200.
  const { data: storefronts } = useStorefronts({ limit: 200 });

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      <Field label="Order ID">
        <DebouncedInput
          value={search.order_id ?? ""}
          onCommit={(value) => onChange({ order_id: value || undefined })}
          placeholder="Starts with…"
          className="w-44"
        />
      </Field>

      <Field label="Storefront">
        <FilterSelect
          value={search.storefront_id ?? null}
          onValueChange={(value) => onChange({ storefront_id: value ?? undefined })}
          placeholder="All storefronts"
          className="w-52"
          options={(storefronts?.items ?? []).map((storefront) => ({
            value: storefront.storefront_id,
            label: storefront.storefront_name,
          }))}
        />
      </Field>

      {/* A closed set, unlike the inbound event types: these three are ours,
          produced by models.CallbackEventFor. SETTLED, REFUNDED and INITIATED
          deliberately produce no callback at all. */}
      <Field label="Event">
        <FilterSelect
          value={search.event_type ?? null}
          onValueChange={(value) => onChange({ event_type: value ?? undefined })}
          placeholder="Any event"
          className="w-48"
          options={CALLBACK_EVENT_TYPES.map((event) => ({ value: event, label: event }))}
        />
      </Field>

      <Field label="From">
        <Input
          type="date"
          value={search.start_date ?? ""}
          onChange={(event) => onChange({ start_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      <Field label="To">
        <Input
          type="date"
          value={search.end_date ?? ""}
          onChange={(event) => onChange({ end_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      {hasFilters ? (
        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          <XIcon className="size-3.5" />
          Clear
        </Button>
      ) : null}
    </div>
  );
}
